export type WaitForTargetOptions = {
  timeoutMs?: number;
  intervalMs?: number;
  root?: ParentNode;
};

function resolveSelector(target: string | HTMLElement): string | null {
  if (typeof target !== 'string') return null;
  return target.trim().length > 0 ? target : null;
}

export function isTourTargetInDom(element: Element): boolean {
  return document.body.contains(element);
}

export function isTourTargetVisible(element: Element): boolean {
  if (!isTourTargetInDom(element)) {
    return false;
  }

  const style = window.getComputedStyle(element);
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    Number.parseFloat(style.opacity) === 0
  ) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
}

function pickBestTourTarget(
  candidates: Element[],
): Element | null {
  for (const candidate of candidates) {
    if (isTourTargetVisible(candidate)) {
      return candidate;
    }
  }
  return candidates[0] ?? null;
}

export function queryTourTarget(
  target: string | HTMLElement,
  root: ParentNode = document,
): Element | null {
  if (typeof target !== 'string') {
    return target instanceof Element ? target : null;
  }

  const selector = resolveSelector(target);
  if (!selector) return null;

  if (selector === 'body' || selector === 'html') {
    return document.querySelector(selector);
  }

  const dataTourMatch = selector.match(/data-tour(?:-stable)?=["']([^"']+)["']/);
  if (dataTourMatch) {
    const id = dataTourMatch[1];
    const stableMatches = Array.from(
      root.querySelectorAll(`[data-tour-stable="${id}"]`),
    );
    if (stableMatches.length > 0) {
      return pickBestTourTarget(stableMatches);
    }
    const tourMatches = Array.from(root.querySelectorAll(`[data-tour="${id}"]`));
    if (tourMatches.length > 0) {
      return pickBestTourTarget(tourMatches);
    }
    return null;
  }

  try {
    return root.querySelector(selector);
  } catch {
    return null;
  }
}

export async function waitForTarget(
  target: string | HTMLElement,
  options: WaitForTargetOptions = {},
): Promise<Element | null> {
  const timeoutMs = options.timeoutMs ?? 5000;
  const intervalMs = options.intervalMs ?? 100;
  const root = options.root ?? document;
  const started = Date.now();

  const existing = queryTourTarget(target, root);
  if (existing && isTourTargetVisible(existing)) return existing;

  return new Promise((resolve) => {
    const observerRoot = root instanceof Document ? document.body : (root as Element);
    let settled = false;

    const finish = (element: Element | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearInterval(poll);
      resolve(element);
    };

    const poll = window.setInterval(() => {
      const element = queryTourTarget(target, root);
      if (element && isTourTargetVisible(element)) {
        finish(element);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        finish(null);
      }
    }, intervalMs);

    const observer = new MutationObserver(() => {
      const element = queryTourTarget(target, root);
      if (element && isTourTargetVisible(element)) {
        finish(element);
      }
    });

    observer.observe(observerRoot, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    window.setTimeout(() => {
      const element = queryTourTarget(target, root);
      finish(element && isTourTargetVisible(element) ? element : null);
    }, timeoutMs);
  });
}
